import { GridStack, GridStackOptions, GridStackNode } from 'gridstack';

export class GridStackLayout {

    grid: GridStack | undefined;
    options: any | undefined;
    context: any | undefined;

    // Auto-scroll while dragging near the top/bottom edge of the scroll container.
    private readonly edgeSize = 60;   // px zone from the edge where auto-scroll starts
    private readonly maxSpeed = 18;   // px per frame at the very edge
    private scrollContainer: HTMLElement | Window = window;
    private scrollRafId: number | null = null;
    private pointerX = 0;
    private pointerY = 0;

    constructor() {
        this.grid = undefined;
        this.options = null;
    }

    layout(ref: any, context: any) {
        this.options = context.options;
        this.context = context;
        // Match the read-only CSS grid (grid-auto-rows: rowHeight; gap). Gridstack has no separate
        // row-gap: cellHeight is the full row pitch and the gap comes from item margins (gap = 2 x margin).
        const rowHeight = this.options.rowHeight;
        const gap = this.options.gap;
        let gridOptions: GridStackOptions = {
            float: this.options.floating,
            disableDrag: !this.options.isEditable,
            disableResize: !this.options.isEditable,
            draggable: {
                handle: this.options.draggableHandle,
                // Built-in auto-scroll only fires when the dragged element itself clips the edge, which
                // barely happens when grabbing a card by its header. We run a cursor-proximity scroll instead.
                scroll: false
            },
            resizable: {
                handles: 'se, sw'
            },
            cellHeight: (rowHeight + gap) + 'px',
            margin: (gap / 2) + 'px',
            acceptWidgets: true
        };

        const dialogContainerSelector = this.options.dialogContainerSelector;
        if (dialogContainerSelector)
            document.querySelector(dialogContainerSelector)?.children[1]?.classList.add('composite-dialog');

        const grid = GridStack.init(gridOptions, ref);
        if (!grid)
            return;
        this.grid = grid;
        this.grid.on('change', (_event: Event, items?: GridStackNode[]) => this.onChange(items ?? []));
        this.grid.on('dragstart', this.startAutoScroll);
        this.grid.on('dragstop', this.stopAutoScroll);
    }

    appendElement(_ref: any, id: string) {
        let element = document.getElementById(id);
        if (element)
            this.grid?.makeWidget(element);
    }

    removeWidget(id: string) {
        let element = document.getElementById(id);
        if (element)
            this.grid?.removeWidget(element);
    }

    onChange(items: GridStackNode[]) {
        let compositeHeight = 0;
        let compositeId = document.querySelector('div[composite-id]')?.getAttribute('composite-id');
        if (compositeId != null)
            compositeHeight = this.grid?.getRow() ?? 0;

        let layouts = items.map(i => ({ 'id': i.el?.id, 'w': i.w, 'h': i.h, 'x': i.x, 'y': i.y }));
        return this.context.onLayoutChangeCaller
            .invokeMethodAsync(this.context.onLayoutChangeFn, layouts, compositeId, compositeHeight == 0 ? 4 : compositeHeight)
            .then((r: any) => console.log(r));
    }

    /** Nearest scrollable ancestor of the grid; falls back to the window (main dashboard scrolls the page). */
    private getScrollParent(el: HTMLElement | null | undefined): HTMLElement | Window {
        let node = el?.parentElement;
        while (node) {
            const style = getComputedStyle(node);
            if ((style.overflowY === 'auto' || style.overflowY === 'scroll') && node.scrollHeight > node.clientHeight)
                return node;
            node = node.parentElement;
        }
        return window;
    }

    private trackPointer = (e: MouseEvent) => {
        this.pointerX = e.clientX;
        this.pointerY = e.clientY;
    };

    private startAutoScroll = (event: Event) => {
        const mouse = event as MouseEvent;
        if (typeof mouse.clientX === 'number') {
            this.pointerX = mouse.clientX;
            this.pointerY = mouse.clientY;
        }
        this.scrollContainer = this.getScrollParent(this.grid?.el);
        document.addEventListener('mousemove', this.trackPointer, { capture: true, passive: true });
        if (this.scrollRafId == null)
            this.scrollRafId = requestAnimationFrame(this.autoScrollTick);
    };

    private stopAutoScroll = () => {
        document.removeEventListener('mousemove', this.trackPointer, true);
        if (this.scrollRafId != null) {
            cancelAnimationFrame(this.scrollRafId);
            this.scrollRafId = null;
        }
    };

    private autoScrollTick = () => {
        const container = this.scrollContainer;
        let top: number, bottom: number, scrollTop: number, maxScroll: number;
        if (container === window) {
            top = 0;
            bottom = window.innerHeight;
            scrollTop = window.scrollY;
            maxScroll = document.documentElement.scrollHeight - window.innerHeight;
        } else {
            const el = container as HTMLElement;
            const rect = el.getBoundingClientRect();
            top = rect.top;
            bottom = rect.bottom;
            scrollTop = el.scrollTop;
            maxScroll = el.scrollHeight - el.clientHeight;
        }

        let delta = 0;
        const distTop = this.pointerY - top;
        const distBottom = bottom - this.pointerY;
        if (distTop < this.edgeSize && scrollTop > 0)
            delta = -this.maxSpeed * (1 - Math.max(distTop, 0) / this.edgeSize);
        else if (distBottom < this.edgeSize && scrollTop < maxScroll)
            delta = this.maxSpeed * (1 - Math.max(distBottom, 0) / this.edgeSize);

        if (delta !== 0) {
            if (container === window)
                window.scrollBy(0, delta);
            else
                (container as HTMLElement).scrollTop += delta;
            // The container scrolled under a possibly-stationary cursor: replay the pointer position so
            // gridstack recomputes the drop target and the placeholder keeps following.
            document.dispatchEvent(new MouseEvent('mousemove', {
                clientX: this.pointerX,
                clientY: this.pointerY,
                bubbles: true,
                cancelable: true,
                view: window
            }));
        }

        this.scrollRafId = requestAnimationFrame(this.autoScrollTick);
    };
}
